import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import { Box, Button, Stack, Typography } from "@mui/material";

const BRAND_BLUE = "#00336C";

export default function HelpChatMessage({ message, onLinkClick, onFeedback }) {
  const isUserMessage = message.role === "user";
  const links = !isUserMessage && Array.isArray(message.links) ? message.links : [];
  const sources = !isUserMessage && Array.isArray(message.sources) ? message.sources : [];
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackError, setFeedbackError] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const sourceLabels = sources
    .map((source) => source?.label)
    .filter(Boolean);
  const canSubmitFeedback = Boolean(!isUserMessage && message.responseId && onFeedback);

  const handleFeedback = async (rating) => {
    if (!canSubmitFeedback || selectedFeedback || isSubmittingFeedback) {
      return;
    }

    setFeedbackError("");
    setIsSubmittingFeedback(true);
    try {
      await onFeedback(message.responseId, rating);
      setSelectedFeedback(rating);
    } catch (error) {
      console.error("Help feedback submission failed", error);
      setFeedbackError("Feedback could not be saved.");
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUserMessage ? "flex-end" : "flex-start",
      }}
    >
      <Box
        sx={{
          maxWidth: "82%",
          px: 1.5,
          py: 1.1,
          borderRadius: 2,
          backgroundColor: isUserMessage ? BRAND_BLUE : "#fff",
          border: isUserMessage ? "1px solid transparent" : "1px solid rgba(0,51,108,0.12)",
          color: isUserMessage ? "#fff" : BRAND_BLUE,
          boxShadow: isUserMessage ? "none" : "0 6px 18px rgba(15,23,42,0.06)",
        }}
      >
        {!isUserMessage && (
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 900, mb: 0.35 }}>
            ProspectEngage Assistant
          </Typography>
        )}
        <Typography sx={{ fontSize: "0.88rem", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
          {message.content}
        </Typography>
        {sourceLabels.length > 0 && (
          <Typography
            sx={{
              color: "rgba(0,51,108,0.62)",
              fontSize: "0.72rem",
              fontWeight: 700,
              mt: 0.85,
            }}
          >
            Based on: {sourceLabels.join(", ")}
          </Typography>
        )}
        {links.length > 0 && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            {links.map((link) => (
              <Button
                key={`${link.path}-${link.label}`}
                component={RouterLink}
                to={link.path}
                onClick={onLinkClick}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: "rgba(0,51,108,0.24)",
                  color: BRAND_BLUE,
                  fontSize: "0.74rem",
                  fontWeight: 800,
                  lineHeight: 1.2,
                  px: 1,
                  py: 0.45,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "rgba(0,51,108,0.42)",
                    backgroundColor: "rgba(0,51,108,0.04)",
                  },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>
        )}
        {canSubmitFeedback && (
          <Box sx={{ mt: 1.1 }}>
            <Typography
              sx={{
                color: "rgba(0,51,108,0.58)",
                fontSize: "0.7rem",
                fontWeight: 700,
                mb: 0.4,
              }}
            >
              Was this helpful?
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              <Button
                size="small"
                startIcon={<ThumbUpOffAltIcon fontSize="inherit" />}
                disabled={isSubmittingFeedback || Boolean(selectedFeedback && selectedFeedback !== "helpful")}
                onClick={() => handleFeedback("helpful")}
                variant={selectedFeedback === "helpful" ? "contained" : "text"}
                sx={{
                  color: selectedFeedback === "helpful" ? "#fff" : "rgba(0,51,108,0.72)",
                  backgroundColor: selectedFeedback === "helpful" ? BRAND_BLUE : "transparent",
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  minWidth: 0,
                  px: 0.8,
                  py: 0.2,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: selectedFeedback === "helpful" ? BRAND_BLUE : "rgba(0,51,108,0.04)",
                  },
                }}
              >
                Helpful
              </Button>
              <Button
                size="small"
                startIcon={<ThumbDownOffAltIcon fontSize="inherit" />}
                disabled={isSubmittingFeedback || Boolean(selectedFeedback && selectedFeedback !== "not_helpful")}
                onClick={() => handleFeedback("not_helpful")}
                variant={selectedFeedback === "not_helpful" ? "contained" : "text"}
                sx={{
                  color: selectedFeedback === "not_helpful" ? "#fff" : "rgba(0,51,108,0.72)",
                  backgroundColor: selectedFeedback === "not_helpful" ? BRAND_BLUE : "transparent",
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  minWidth: 0,
                  px: 0.8,
                  py: 0.2,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: selectedFeedback === "not_helpful" ? BRAND_BLUE : "rgba(0,51,108,0.04)",
                  },
                }}
              >
                Not Helpful
              </Button>
            </Stack>
            {feedbackError && (
              <Typography sx={{ color: "#9f1239", fontSize: "0.7rem", mt: 0.4 }}>
                {feedbackError}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
